import type { CSSProperties, ReactElement } from 'react';

interface IconProps {
  style?: CSSProperties;
  width?: string | number;
  height?: string | number;
}

export function CheckmarkIcon({
  style,
  width = 18,
  height = 18,
}: IconProps): ReactElement {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      width={width}
      height={height}
      style={style}
      aria-hidden="true"
    >
      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
    </svg>
  );
}

export function ChevronDownIcon({
  style,
  width = 18,
  height = 18,
}: IconProps): ReactElement {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      width={width}
      height={height}
      style={style}
      aria-hidden="true"
    >
      <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" />
    </svg>
  );
}

export function DocumentIcon({
  style,
  width = 18,
  height = 24,
}: IconProps): ReactElement {
  return (
    <svg
      viewBox="0 0 18 24"
      fill="none"
      width={width}
      height={height}
      style={style}
      aria-hidden="true"
    >
      <path
        d="M11.25 0H2.25C1.00781 0 0 1.00781 0 2.25V21.75C0 22.9922 1.00781 24 2.25 24H15.75C16.9922 24 18 22.9922 18 21.75V6.75L11.25 0ZM15.75 21.75H2.25V2.25H10.125V7.875H15.75V21.75Z"
        fill="currentColor"
      />
    </svg>
  );
}

export function ImagePlaceholderIcon({
  style,
  width = 32,
  height = 32,
  strokeWidth = 1.5,
}: IconProps & { strokeWidth?: number }): ReactElement {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      style={style}
      aria-hidden="true"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <path d="m21 15-5-5L5 21" />
    </svg>
  );
}

// Matches Font Awesome Classic Solid's "arrow-up-right-from-square" glyph
// (viewBox/path from @fortawesome/free-solid-svg-icons' faArrowUpRightFromSquare).
// Hand-drawn rather than a FontAwesomeIcon import: packages/ui-blocks (unlike
// apps/authoring) has never depended on @fortawesome, and this package ships
// inside every exported SCORM package, so a new runtime dependency here would
// add to every export's bundle size rather than just this repo's dev build.
export function LinkIcon({
  style,
  width = 20,
  height = 20,
}: IconProps): ReactElement {
  return (
    <svg
      viewBox="0 0 512 512"
      fill="currentColor"
      width={width}
      height={height}
      style={style}
      aria-hidden="true"
    >
      <path d="M320 0c-17.7 0-32 14.3-32 32s14.3 32 32 32l82.7 0-201.4 201.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L448 109.3 448 192c0 17.7 14.3 32 32 32s32-14.3 32-32l0-160c0-17.7-14.3-32-32-32L320 0zM80 96C35.8 96 0 131.8 0 176L0 432c0 44.2 35.8 80 80 80l256 0c44.2 0 80-35.8 80-80l0-80c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 80c0 8.8-7.2 16-16 16L80 448c-8.8 0-16-7.2-16-16l0-256c0-8.8 7.2-16 16-16l80 0c17.7 0 32-14.3 32-32s-14.3-32-32-32L80 96z" />
    </svg>
  );
}

export function ComparisonSliderKnobIcon({
  style,
  width = 35,
  height = 35,
  fill = 'currentColor',
  stroke = '#FFFFFF',
}: IconProps & { fill?: string; stroke?: string }): ReactElement {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 35 35"
      fill="none"
      style={style}
      aria-hidden="true"
    >
      <rect width="35" height="35" rx="17.5" fill={fill} />
      <path
        d="M21 10L13 18L21 26"
        stroke={stroke}
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}
